import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Save, Loader2 } from "lucide-react";

// Paleta oficial de Sur Primicia (misma que tailwind.config.js).
const SITE_PALETTE = [
  { key: "petroleum", name: "Petróleo", hex: "#073B4C" },
  { key: "nightblue", name: "Noche", hex: "#071E33" },
  { key: "blueblack", name: "Negro azulado", hex: "#101820" },
  { key: "turquoise", name: "Turquesa", hex: "#00A6A6" },
  { key: "watergreen", name: "Verde agua", hex: "#39C6A3" },
  { key: "softblue", name: "Azul suave", hex: "#A8DADC" },
  { key: "lightgray", name: "Gris claro", hex: "#F3F5F6" },
  { key: "textgray", name: "Gris texto", hex: "#4B5563" },
  { key: "white", name: "Blanco", hex: "#FFFFFF" },
];

// Reusable colour field: palette swatches + native color picker + hex text
// input + reset button. Used across all header/category/ticker colour fields.
function ColorField({ label, testIdPrefix, value, onChange, defaultValue }) {
  const normalized = (value || "").toLowerCase();
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">{label}</Label>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {SITE_PALETTE.map((c) => {
          const selected = normalized === c.hex.toLowerCase();
          return (
            <button
              key={c.hex}
              type="button"
              data-testid={`${testIdPrefix}-${c.key}`}
              onClick={() => onChange(c.hex)}
              title={`${c.name} (${c.hex})`}
              className={`w-8 h-8 border-2 transition-all ${
                selected ? "border-turquoise scale-110 shadow" : "border-softblue hover:border-nightblue"
              }`}
              style={{ backgroundColor: c.hex }}
              aria-label={c.name}
            />
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <input
          type="color"
          data-testid={testIdPrefix}
          value={value || defaultValue}
          onChange={(e) => onChange(e.target.value)}
          className="w-12 h-9 border border-softblue cursor-pointer bg-white p-1"
          aria-label={`Selector de color: ${label}`}
        />
        <Input
          data-testid={`${testIdPrefix}-hex`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={defaultValue}
          className="h-9 bg-lightgray border-softblue font-mono w-36 text-sm"
        />
        <button
          type="button"
          onClick={() => onChange(defaultValue)}
          className="text-[11px] uppercase tracking-widest text-turquoise hover:text-nightblue"
        >
          Restablecer
        </button>
      </div>
    </div>
  );
}

export default function AdminSettingsPage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.siteSettings()
      .then((s) => {
        setForm({
          site_name: s.site_name || "",
          tagline: s.tagline || "",
          logo_url: s.logo_url || "",
          contact_email: s.contact_email || "",
          contact_phone: s.contact_phone || "",
          address: s.address || "",
          footer_text: s.footer_text || "",
          social: {
            facebook: s.social?.facebook || "",
            twitter: s.social?.twitter || "",
            instagram: s.social?.instagram || "",
            youtube: s.social?.youtube || "",
          },
          // SEO
          seo_default_title: s.seo_default_title || "",
          seo_default_description: s.seo_default_description || "",
          default_og_image: s.default_og_image || "",
          twitter_handle: s.twitter_handle || "",
          facebook_app_id: s.facebook_app_id || "",
          publisher_logo_url: s.publisher_logo_url || "",
          public_site_url: s.public_site_url || "",
          robots_txt: s.robots_txt || "",
          google_site_verification: s.google_site_verification || "",
          google_analytics_id: s.google_analytics_id || "",
          header_brand_bg_color: s.header_brand_bg_color || "#073B4C",
          header_size: s.header_size || "normal",
          header_height_desktop: s.header_height_desktop ?? 76,
          header_height_tablet: s.header_height_tablet ?? 68,
          header_height_mobile: s.header_height_mobile ?? 64,
          logo_width_desktop: s.logo_width_desktop ?? 240,
          logo_width_tablet: s.logo_width_tablet ?? 210,
          logo_width_mobile: s.logo_width_mobile ?? 180,
          header_side_padding_desktop: s.header_side_padding_desktop ?? 24,
          header_side_padding_mobile: s.header_side_padding_mobile ?? 18,
          header_logo_menu_gap: s.header_logo_menu_gap ?? 32,
          category_bar_bg_color: s.category_bar_bg_color || "#071E33",
          category_bar_text_color: s.category_bar_text_color || "#FFFFFF",
          ticker_bg_color: s.ticker_bg_color || "#00A6A6",
          ticker_text_color: s.ticker_text_color || "#FFFFFF",
          about_title: s.about_title || "",
          about_content: s.about_content || "",
          privacy_title: s.privacy_title || "",
          privacy_content: s.privacy_content || "",
          social_logo_scale_default: s.social_logo_scale_default ?? 0.5,
        });
      })
      .catch(() => toast.error("No se pudo cargar la configuración."));
  }, []);

  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setSocial = (patch) => setForm((f) => ({ ...f, social: { ...f.social, ...patch } }));

  const save = async () => {
    if (!form.site_name.trim()) { toast.error("El nombre del portal es obligatorio."); return; }
    setSaving(true);
    try {
      // Strip UI-only fields (prefixed with "_") before sending to backend.
      const payload = Object.fromEntries(
        Object.entries(form).filter(([k]) => !k.startsWith("_"))
      );
      await api.adminUpdateSettings(payload);
      toast.success("Configuración actualizada.");
    } catch {
      toast.error("No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="p-4 text-sm text-textgray">Cargando configuración…</div>;
  }

  return (
    <div data-testid={ATID.settingsPage}>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-turquoise font-semibold">Ajustes</div>
          <h1 className="font-serif font-black text-3xl sm:text-4xl text-blueblack">Configuración</h1>
        </div>
        <Button
          data-testid={ATID.settingsSubmit}
          onClick={save}
          disabled={saving}
          className="bg-nightblue hover:bg-blueblack text-white rounded-none h-10 px-5 uppercase text-xs tracking-widest font-semibold"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar cambios
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-softblue p-6 space-y-4">
          <h2 className="font-serif text-xl font-bold text-blueblack">Identidad</h2>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Nombre del portal *</Label>
            <Input
              data-testid={ATID.settingsSiteName}
              value={form.site_name}
              onChange={(e) => setField({ site_name: e.target.value })}
              className="mt-2 h-10 bg-lightgray border-softblue"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Descripción / bajada</Label>
            <Textarea
              data-testid={ATID.settingsTagline}
              value={form.tagline}
              onChange={(e) => setField({ tagline: e.target.value })}
              rows={2}
              className="mt-2 bg-lightgray border-softblue"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Logo del portal (URL)</Label>
            <Input
              data-testid="settings-logo-url"
              value={form.logo_url}
              onChange={(e) => setField({ logo_url: e.target.value })}
              placeholder="https://…/logo.png"
              className="mt-2 h-10 bg-lightgray border-softblue"
            />
            <p className="text-[11px] text-textgray mt-1.5">
              Se muestra en el header del portal en lugar del texto {'"SurPrimicia"'}. Recomendado: PNG/SVG horizontal ~200 × 60 px. Subilo primero en <a href="/admin/media" className="text-turquoise underline">Multimedia</a> y pegá la URL acá.
            </p>
            {form.logo_url && (
              <div className="mt-3 border border-softblue bg-white p-3 inline-block">
                <img src={form.logo_url} alt="Preview logo" className="h-12 max-w-[240px] object-contain" onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Texto del pie de página</Label>
            <Textarea
              data-testid={ATID.settingsFooter}
              value={form.footer_text}
              onChange={(e) => setField({ footer_text: e.target.value })}
              rows={3}
              placeholder="Texto opcional que aparece en el footer del portal."
              className="mt-2 bg-lightgray border-softblue"
            />
          </div>
        </div>

        <div className="bg-white border border-softblue p-6 space-y-4">
          <h2 className="font-serif text-xl font-bold text-blueblack">Contacto</h2>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Correo</Label>
            <Input
              data-testid={ATID.settingsEmail}
              type="email"
              value={form.contact_email}
              onChange={(e) => setField({ contact_email: e.target.value })}
              className="mt-2 h-10 bg-lightgray border-softblue"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Teléfono</Label>
            <Input
              data-testid={ATID.settingsPhone}
              value={form.contact_phone}
              onChange={(e) => setField({ contact_phone: e.target.value })}
              className="mt-2 h-10 bg-lightgray border-softblue"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Dirección</Label>
            <Input
              data-testid={ATID.settingsAddress}
              value={form.address}
              onChange={(e) => setField({ address: e.target.value })}
              className="mt-2 h-10 bg-lightgray border-softblue"
            />
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-softblue p-6 space-y-4">
          <h2 className="font-serif text-xl font-bold text-blueblack">Redes sociales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Facebook</Label>
              <Input
                data-testid={ATID.settingsFacebook}
                value={form.social.facebook}
                onChange={(e) => setSocial({ facebook: e.target.value })}
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">X (Twitter)</Label>
              <Input
                data-testid={ATID.settingsTwitter}
                value={form.social.twitter}
                onChange={(e) => setSocial({ twitter: e.target.value })}
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Instagram</Label>
              <Input
                data-testid={ATID.settingsInstagram}
                value={form.social.instagram}
                onChange={(e) => setSocial({ instagram: e.target.value })}
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">YouTube</Label>
              <Input
                data-testid={ATID.settingsYoutube}
                value={form.social.youtube}
                onChange={(e) => setSocial({ youtube: e.target.value })}
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-softblue p-6 space-y-4">
          <h2 className="font-serif text-xl font-bold text-blueblack">SEO y redes sociales</h2>
          <p className="text-sm text-textgray">
            Estos ajustes se usan cuando una noticia o categoría no tiene su propio SEO cargado.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">URL pública del sitio</Label>
              <Input
                data-testid={ATID.settingsPublicSiteUrl}
                value={form.public_site_url}
                onChange={(e) => setField({ public_site_url: e.target.value })}
                placeholder="https://www.surprimicia.com.ar"
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
              <div className="text-[10px] text-textgray mt-1">
                Usada en URLs canonical, sitemap, RSS y robots.txt. Sin esto se usa el host del request.
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Título SEO por defecto</Label>
              <Input
                data-testid={ATID.settingsSeoDefaultTitle}
                value={form.seo_default_title}
                onChange={(e) => setField({ seo_default_title: e.target.value })}
                placeholder="Sur Primicia — Noticias de Tierra del Fuego"
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Descripción SEO por defecto</Label>
              <Textarea
                data-testid={ATID.settingsSeoDefaultDescription}
                value={form.seo_default_description}
                onChange={(e) => setField({ seo_default_description: e.target.value })}
                rows={2}
                placeholder="Portal digital de Tierra del Fuego con noticias, política, deportes, cultura y más."
                className="mt-2 bg-lightgray border-softblue"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Imagen Open Graph por defecto (URL)</Label>
              <Input
                data-testid={ATID.settingsDefaultOgImage}
                value={form.default_og_image}
                onChange={(e) => setField({ default_og_image: e.target.value })}
                placeholder="https://…/og-default.jpg (1200×630)"
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Handle de X / Twitter</Label>
              <Input
                data-testid={ATID.settingsTwitterHandle}
                value={form.twitter_handle}
                onChange={(e) => setField({ twitter_handle: e.target.value })}
                placeholder="@surprimicia"
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Facebook App ID</Label>
              <Input
                data-testid={ATID.settingsFacebookAppId}
                value={form.facebook_app_id}
                onChange={(e) => setField({ facebook_app_id: e.target.value })}
                placeholder="123456789012345"
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Logo del publisher (URL)</Label>
              <Input
                data-testid={ATID.settingsPublisherLogo}
                value={form.publisher_logo_url}
                onChange={(e) => setField({ publisher_logo_url: e.target.value })}
                placeholder="https://…/logo.png"
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
              <div className="text-[10px] text-textgray mt-1">Usado en JSON-LD (schema.org NewsMediaOrganization).</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Google Search Console (verification)</Label>
              <Input
                data-testid={ATID.settingsGoogleVerification}
                value={form.google_site_verification}
                onChange={(e) => setField({ google_site_verification: e.target.value })}
                placeholder="Contenido del meta google-site-verification"
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Google Analytics ID</Label>
              <Input
                data-testid={ATID.settingsGoogleAnalytics}
                value={form.google_analytics_id}
                onChange={(e) => setField({ google_analytics_id: e.target.value })}
                placeholder="G-XXXXXXXX"
                className="mt-2 h-10 bg-lightgray border-softblue"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-softblue p-6 space-y-4">
          <div>
            <h2 className="font-serif text-xl font-bold text-blueblack">Editor de fotos (Instagram)</h2>
            <p className="text-sm text-textgray mt-1">
              Ajustes globales para las placas generadas para redes sociales.
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">
                Tamaño por defecto del logo
              </Label>
              <span className="text-[11px] font-mono text-turquoise">
                {Math.round((form.social_logo_scale_default ?? 0.5) * 100)}%
              </span>
            </div>
            <input
              data-testid="settings-social-logo-scale"
              type="range"
              min="15"
              max="75"
              value={Math.round((form.social_logo_scale_default ?? 0.5) * 100)}
              onChange={(e) => setField({ social_logo_scale_default: parseInt(e.target.value, 10) / 100 })}
              className="w-full accent-turquoise"
            />
            <div className="text-[11px] text-textgray mt-1">
              Ancho del logo respecto del ancho total de la placa. Se aplica a todas las placas nuevas cuando no hay preset ni override explícito.
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-softblue p-6 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-serif text-xl font-bold text-blueblack">robots.txt</h2>
              <p className="text-sm text-textgray mt-1">
                Personalizá el archivo que sirve <code className="text-xs bg-lightgray px-1">/api/robots.txt</code>.
                Si lo dejás vacío se usa un default seguro que bloquea <code className="text-xs bg-lightgray px-1">/admin</code> y expone los sitemaps.
              </p>
            </div>
            <a
              href="/api/robots.txt"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs uppercase tracking-widest text-turquoise hover:text-nightblue"
            >
              Ver robots.txt →
            </a>
          </div>
          <Textarea
            data-testid={ATID.settingsRobotsTxt}
            value={form.robots_txt}
            onChange={(e) => setField({ robots_txt: e.target.value })}
            rows={10}
            placeholder="Dejar vacío para usar el default."
            className="bg-lightgray border-softblue font-mono text-xs"
          />
          <div className="flex flex-wrap gap-3 text-xs text-textgray pt-2">
            <a href="/api/sitemap.xml" target="_blank" rel="noopener noreferrer" className="hover:text-turquoise">/api/sitemap.xml</a>
            <a href="/api/sitemap-noticias.xml" target="_blank" rel="noopener noreferrer" className="hover:text-turquoise">/api/sitemap-noticias.xml</a>
            <a href="/api/sitemap-categorias.xml" target="_blank" rel="noopener noreferrer" className="hover:text-turquoise">/api/sitemap-categorias.xml</a>
            <a href="/api/sitemap-news.xml" target="_blank" rel="noopener noreferrer" className="hover:text-turquoise">/api/sitemap-news.xml</a>
            <a href="/api/rss.xml" target="_blank" rel="noopener noreferrer" className="hover:text-turquoise">/api/rss.xml</a>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-softblue p-6 space-y-6">
          <div>
            <h2 className="font-serif text-xl font-bold text-blueblack">Apariencia del header</h2>
            <p className="text-sm text-textgray mt-1">
              Configurá los colores de cada franja del encabezado. Los cambios se aplican al portal al guardar.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">
              Dimensiones del header
            </Label>
            <p className="text-[11px] text-textgray mt-1 mb-4">
              El alto del header y el ancho del logo son independientes. El logo usa <code className="bg-lightgray px-1">height: auto</code>, se dimensiona sólo por ancho.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { device: "desktop", label: "Desktop", h: "header_height_desktop", w: "logo_width_desktop", hMin: 56, hMax: 160, wMin: 100, wMax: 480 },
                { device: "tablet", label: "Tablet", h: "header_height_tablet", w: "logo_width_tablet", hMin: 52, hMax: 140, wMin: 80, wMax: 400 },
                { device: "mobile", label: "Mobile", h: "header_height_mobile", w: "logo_width_mobile", hMin: 48, hMax: 120, wMin: 60, wMax: 320 },
              ].map((row) => (
                <div key={row.device} className="border border-softblue p-3 bg-lightgray/40 space-y-3">
                  <div className="text-[11px] uppercase tracking-widest font-bold text-nightblue">{row.label}</div>
                  <div>
                    <Label className="text-[11px] text-textgray">Alto del header (px)</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="range"
                        min={row.hMin} max={row.hMax} step={1}
                        value={form[row.h] ?? 76}
                        onChange={(e) => setField({ [row.h]: parseInt(e.target.value, 10) })}
                        className="flex-1"
                        data-testid={`admin-settings-${row.h.replace(/_/g, "-")}-range`}
                      />
                      <Input
                        type="number"
                        min={row.hMin} max={row.hMax}
                        value={form[row.h] ?? 76}
                        onChange={(e) => setField({ [row.h]: parseInt(e.target.value, 10) || 0 })}
                        className="h-8 w-20 text-center bg-white border-softblue"
                        data-testid={`admin-settings-${row.h.replace(/_/g, "-")}`}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px] text-textgray">Ancho del logo (px)</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="range"
                        min={row.wMin} max={row.wMax} step={1}
                        value={form[row.w] ?? 240}
                        onChange={(e) => setField({ [row.w]: parseInt(e.target.value, 10) })}
                        className="flex-1"
                        data-testid={`admin-settings-${row.w.replace(/_/g, "-")}-range`}
                      />
                      <Input
                        type="number"
                        min={row.wMin} max={row.wMax}
                        value={form[row.w] ?? 240}
                        onChange={(e) => setField({ [row.w]: parseInt(e.target.value, 10) || 0 })}
                        className="h-8 w-20 text-center bg-white border-softblue"
                        data-testid={`admin-settings-${row.w.replace(/_/g, "-")}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <Label className="text-[11px] text-textgray uppercase tracking-widest">Padding lateral desktop (px)</Label>
                <Input
                  type="number" min={0} max={80}
                  value={form.header_side_padding_desktop ?? 24}
                  onChange={(e) => setField({ header_side_padding_desktop: parseInt(e.target.value, 10) || 0 })}
                  className="h-9 w-full mt-1 bg-white border-softblue"
                  data-testid="admin-settings-header-side-padding-desktop"
                />
              </div>
              <div>
                <Label className="text-[11px] text-textgray uppercase tracking-widest">Padding lateral mobile (px)</Label>
                <Input
                  type="number" min={0} max={60}
                  value={form.header_side_padding_mobile ?? 18}
                  onChange={(e) => setField({ header_side_padding_mobile: parseInt(e.target.value, 10) || 0 })}
                  className="h-9 w-full mt-1 bg-white border-softblue"
                  data-testid="admin-settings-header-side-padding-mobile"
                />
              </div>
              <div>
                <Label className="text-[11px] text-textgray uppercase tracking-widest">Separación logo ↔ menú (px)</Label>
                <Input
                  type="number" min={0} max={120}
                  value={form.header_logo_menu_gap ?? 32}
                  onChange={(e) => setField({ header_logo_menu_gap: parseInt(e.target.value, 10) || 0 })}
                  className="h-9 w-full mt-1 bg-white border-softblue"
                  data-testid="admin-settings-header-logo-menu-gap"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => setField({
                header_height_desktop: 76, header_height_tablet: 68, header_height_mobile: 64,
                logo_width_desktop: 240, logo_width_tablet: 210, logo_width_mobile: 180,
                header_side_padding_desktop: 24, header_side_padding_mobile: 18,
                header_logo_menu_gap: 32,
              })}
              className="mt-3 text-[11px] uppercase tracking-widest text-turquoise hover:text-nightblue"
            >
              Restablecer valores recomendados
            </button>

            {/* Preview live: header con los valores actuales del form. Tabs Desktop/Tablet/Mobile. */}
            <div className="mt-4 border border-softblue">
              <div className="flex items-center justify-between px-4 py-2 bg-lightgray border-b border-softblue">
                <span className="text-[11px] uppercase tracking-[0.2em] text-textgray">Vista previa</span>
                <div className="flex gap-1" data-testid="header-preview-tabs">
                  {[
                    { key: "desktop", label: "Desktop", w: 1200 },
                    { key: "tablet", label: "Tablet", w: 768 },
                    { key: "mobile", label: "Mobile", w: 390 },
                  ].map((t) => {
                    const active = (form._preview_device || "desktop") === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        data-testid={`header-preview-tab-${t.key}`}
                        onClick={() => setField({ _preview_device: t.key })}
                        className={`text-[11px] uppercase tracking-widest px-3 py-1 border ${
                          active
                            ? "bg-nightblue text-white border-nightblue"
                            : "bg-white text-nightblue border-softblue hover:border-nightblue"
                        }`}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {(() => {
                const dev = form._preview_device || "desktop";
                const h = dev === "mobile" ? (form.header_height_mobile || 64)
                       : dev === "tablet" ? (form.header_height_tablet || 68)
                       : (form.header_height_desktop || 76);
                const w = dev === "mobile" ? (form.logo_width_mobile || 180)
                       : dev === "tablet" ? (form.logo_width_tablet || 210)
                       : (form.logo_width_desktop || 240);
                const pad = dev === "desktop" ? (form.header_side_padding_desktop || 24)
                       : (form.header_side_padding_mobile || 18);
                const gap = form.header_logo_menu_gap ?? 32;
                const viewportW = dev === "mobile" ? 390 : dev === "tablet" ? 768 : 1200;
                return (
                  <div className="bg-lightgray p-4 flex justify-center overflow-x-auto">
                    <div
                      className="border border-softblue bg-white"
                      style={{ width: `${viewportW}px`, maxWidth: "100%" }}
                    >
                      <div
                        className="flex items-center justify-between"
                        style={{
                          backgroundColor: form.header_brand_bg_color || "#073B4C",
                          minHeight: `${h}px`,
                          paddingLeft: `${pad}px`,
                          paddingRight: `${pad}px`,
                          gap: `${gap}px`,
                        }}
                      >
                        <div style={{ width: `${w}px`, maxWidth: `${w}px`, flexShrink: 0 }}>
                          {form.logo_url ? (
                            <img src={form.logo_url} alt="logo" style={{ width: "100%", height: "auto" }} />
                          ) : (
                            <div className="font-serif font-black text-white tracking-tight" style={{ fontSize: `${Math.round(w * 0.16)}px`, lineHeight: 1 }}>
                              Sur<span className="text-turquoise">Primicia</span>
                            </div>
                          )}
                        </div>
                        <div className="text-white/70 text-[10px] whitespace-nowrap">
                          {dev.toUpperCase()} · H {h}px · Logo {w}px · Pad {pad}px · Gap {gap}px
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <ColorField
              label="Fondo de la fila del logo"
              testIdPrefix={ATID.settingsHeaderBrandBg}
              value={form.header_brand_bg_color}
              onChange={(v) => setField({ header_brand_bg_color: v })}
              defaultValue="#073B4C"
            />
            <ColorField
              label="Fondo de la barra de categorías"
              testIdPrefix="admin-settings-category-bar-bg"
              value={form.category_bar_bg_color}
              onChange={(v) => setField({ category_bar_bg_color: v })}
              defaultValue="#071E33"
            />
            <ColorField
              label="Texto de la barra de categorías"
              testIdPrefix="admin-settings-category-bar-text"
              value={form.category_bar_text_color}
              onChange={(v) => setField({ category_bar_text_color: v })}
              defaultValue="#FFFFFF"
            />
            <ColorField
              label="Fondo del ticker (Último momento)"
              testIdPrefix="admin-settings-ticker-bg"
              value={form.ticker_bg_color}
              onChange={(v) => setField({ ticker_bg_color: v })}
              defaultValue="#00A6A6"
            />
            <ColorField
              label="Texto del ticker"
              testIdPrefix="admin-settings-ticker-text"
              value={form.ticker_text_color}
              onChange={(v) => setField({ ticker_text_color: v })}
              defaultValue="#FFFFFF"
            />
          </div>

          <div className="border border-softblue">
            <div className="text-[11px] uppercase tracking-[0.2em] text-textgray px-4 py-2 bg-lightgray border-b border-softblue">
              Vista previa del encabezado
            </div>
            <div
              className="px-5 py-6 flex items-center gap-4"
              style={{ backgroundColor: form.header_brand_bg_color || "#073B4C" }}
            >
              <span className="font-serif font-black text-2xl text-white tracking-tight">
                Sur<span className="text-turquoise">Primicia</span>
              </span>
            </div>
            <div
              className="px-5 py-2 flex items-center gap-6 text-[11px] font-semibold uppercase tracking-[0.14em]"
              style={{
                backgroundColor: form.category_bar_bg_color || "#071E33",
                color: form.category_bar_text_color || "#FFFFFF",
              }}
            >
              <span>Últimas</span><span>Río Grande</span><span>Ushuaia</span><span>Provincial</span>
            </div>
            <div
              className="px-5 py-2 flex items-center gap-3 text-xs"
              style={{
                backgroundColor: form.ticker_bg_color || "#00A6A6",
                color: form.ticker_text_color || "#FFFFFF",
              }}
            >
              <span className="uppercase tracking-widest font-bold text-[11px]">Último momento</span>
              <span>· Titular de ejemplo del ticker en vivo</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-softblue p-6 space-y-4">
          <div>
            <h2 className="font-serif text-xl font-bold text-blueblack">Página &quot;Quiénes somos&quot;</h2>
            <p className="text-sm text-textgray mt-1">
              Contenido editable de la página pública{" "}
              <a href="/quienes-somos" target="_blank" rel="noopener noreferrer" className="text-turquoise hover:text-nightblue">
                /quienes-somos
              </a>
              . Si dejás los campos vacíos, se muestra el texto por defecto. Separá párrafos con una línea en blanco.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Título</Label>
            <Input
              data-testid={ATID.settingsAboutTitle}
              value={form.about_title}
              onChange={(e) => setField({ about_title: e.target.value })}
              placeholder="Quiénes somos"
              className="mt-2 h-10 bg-lightgray border-softblue"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Contenido</Label>
            <Textarea
              data-testid={ATID.settingsAboutContent}
              value={form.about_content}
              onChange={(e) => setField({ about_content: e.target.value })}
              rows={12}
              placeholder="Contá quién es Sur Primicia, la línea editorial y el equipo. Un párrafo por bloque, separados por una línea en blanco."
              className="mt-2 bg-lightgray border-softblue"
            />
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-softblue p-6 space-y-4">
          <div>
            <h2 className="font-serif text-xl font-bold text-blueblack">Página &quot;Políticas de privacidad&quot;</h2>
            <p className="text-sm text-textgray mt-1">
              Contenido editable de la página pública{" "}
              <a href="/politicas-de-privacidad" target="_blank" rel="noopener noreferrer" className="text-turquoise hover:text-nightblue">
                /politicas-de-privacidad
              </a>
              . Si dejás los campos vacíos, se muestra un texto base preparado para Sur Primicia (Ley 25.326, cookies, GA4).
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Título</Label>
            <Input
              data-testid={ATID.settingsPrivacyTitle}
              value={form.privacy_title}
              onChange={(e) => setField({ privacy_title: e.target.value })}
              placeholder="Políticas de privacidad"
              className="mt-2 h-10 bg-lightgray border-softblue"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-textgray font-semibold">Contenido</Label>
            <Textarea
              data-testid={ATID.settingsPrivacyContent}
              value={form.privacy_content}
              onChange={(e) => setField({ privacy_content: e.target.value })}
              rows={18}
              placeholder="Podés dejarlo vacío para usar el texto por defecto o pegar tu propia versión. Separá los párrafos con una línea en blanco."
              className="mt-2 bg-lightgray border-softblue font-mono text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
